import { Icon } from "@iconify/react";
import type { AttachedVia, ClinicianItemStatus, RxNormMatch } from "@/portals/clinician/api/reviewQueueApi";

// Pill styles match the patient review badges (amber "Under review") and the
// admin "Awaiting signup" blue, so the portals read the same.
const STATUS: Partial<Record<ClinicianItemStatus, { label: string; icon: string; className: string }>> = {
  IN_CLINICIAN_REVIEW: { label: "In review", icon: "lucide:stethoscope", className: "bg-[#E8F0FE] text-[#1D4ED8]" },
  NEEDS_MORE_INFO: { label: "Waiting on patient", icon: "lucide:hourglass", className: "bg-[#FEF6DC] text-[#946200]" },
  AWAITING_CLINICIAN: { label: "In shared queue", icon: "lucide:inbox", className: "bg-[#F3F4F6] text-[#4B5563]" },
};

export function ReviewStatusBadge({ status }: { status: ClinicianItemStatus }) {
  const s = STATUS[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.className}`}>
      <Icon icon={s.icon} className="h-3 w-3" />
      {s.label}
    </span>
  );
}

export function AttachedViaTag({ via }: { via: AttachedVia }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[10px] font-bold text-[#4B5563]">
      {via === "ASSIGNED_BY_ADMIN" ? "Assigned" : "Claimed"}
    </span>
  );
}

/** "accept" | "review" | "reject" (backend casing) → "Accept" | "Review" | "Reject". */
export function formatBand(band: string | null): string {
  if (!band) return "—";
  return band.charAt(0).toUpperCase() + band.slice(1).toLowerCase();
}

/** 0–1 → "72%". */
export function formatScore(score: number | null): string {
  return score == null ? "—" : `${Math.round(score * 100)}%`;
}

/**
 * RxNorm match details under a record on the clinician review screen. Match
 * level ("L0"–"L4") is shown as-is. A null rxcui means there was no match,
 * whatever the other fields say.
 */
export function RxNormBlock({ match }: { match: RxNormMatch | null }) {
  const matched = Boolean(match?.rxcui);
  const rows = match && matched
    ? [
        { label: "RxCUI", value: match.rxcui! },
        { label: "Match level", value: match.matchLevel ?? "—" },
        { label: "Confidence", value: formatScore(match.confidenceScore) },
        { label: "Band", value: formatBand(match.confidenceBand) },
      ]
    : [];
  return (
    <section data-testid="rxnorm-block" aria-label="RxNorm match" className="mt-3 rounded-xl border border-[#E5E7EB] bg-white p-3">
      <p className="text-[11px] font-extrabold uppercase tracking-wide text-[#0A2947]">RxNorm match</p>
      {matched ? (
        <dl className="!mb-0 !mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
          {rows.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-[11px] text-[#9CA3AF]">{label}</dt>
              <dd className="!m-0 text-xs font-semibold text-[#0A2947]">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="!mb-0 mt-1 text-xs text-[#9CA3AF]">No RxNorm match</p>
      )}
    </section>
  );
}

/** A review list's Medicine cell — "Unnamed medicine" (muted) when the record has no name, never a blank cell. */
export function MedicineNameCell({ name }: { name: string | null | undefined }) {
  const trimmed = name?.trim();
  return trimmed ? <>{trimmed}</> : <span className="italic text-[#9CA3AF]">Unnamed medicine</span>;
}
